import { Banknote, CreditCard, ShoppingBag, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";

export interface QuickSaleCartLineView {
  lineKey: string;
  name: string;
  articleNumber: string;
  quantity: number;
  unitGrossLabel: string | null;
  lineTotalLabel: string | null;
  pickupDate?: string;
}

export interface QuickSaleLastSaleView {
  invoiceNumber: string;
  amountLabel: string;
  paymentLabel: string;
  pickupNote: { ok: boolean; text: string } | null;
}

export interface QuickSaleCartPanelProps {
  lines: QuickSaleCartLineView[];
  itemCount: number;
  totalLabel: string;
  customerHint: string;
  saleReady: boolean;
  canPay: boolean;
  submitting: boolean;
  dockPayment: boolean;
  /** Leiste am unteren Fensterrand, oder am unteren Rand des umgebenden Modals. */
  dockInset?: "viewport" | "panel";
  zvtConfigured: boolean;
  today: string;
  lastSale: QuickSaleLastSaleView | null;
  onAdjustQty: (lineKey: string, delta: number) => void;
  onRemove: (lineKey: string) => void;
  onPickupDate: (lineKey: string, date: string) => void;
  onClear: () => void;
  onCash: () => void;
  onCard: () => void;
}

export function QuickSaleCartPanel({
  lines,
  itemCount,
  totalLabel,
  customerHint,
  saleReady,
  canPay,
  submitting,
  dockPayment,
  dockInset = "viewport",
  zvtConfigured,
  today,
  lastSale,
  onAdjustQty,
  onRemove,
  onPickupDate,
  onClear,
  onCash,
  onCard,
}: QuickSaleCartPanelProps) {
  const emptyHint = saleReady
    ? "Barcode scannen oder eine Kachel antippen."
    : "Zuerst einen Gast oder Laufkunde wählen.";

  return (
    <aside
      className={clsx(
        "flex flex-col rounded-2xl border border-separator bg-card shadow-ios lg:sticky lg:z-10 lg:overflow-hidden",
        dockInset === "panel"
          ? "lg:top-0 lg:max-h-[min(36rem,calc(94dvh-8rem))]"
          : "lg:top-[calc(var(--eh-app-header-height)+var(--eh-safe-top)+0.75rem)] lg:max-h-[calc(100dvh-var(--eh-app-header-height)-var(--eh-safe-top)-2.75rem)]",
      )}
      aria-label="Warenkorb"
    >
      <header className="flex items-start justify-between gap-3 border-b border-separator px-4 py-3.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-label">Warenkorb</h2>
            {itemCount > 0 ? (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold tabular-nums text-label-on-accent">
                {itemCount}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-xs text-label-secondary">{customerHint}</p>
        </div>
        {lines.length > 0 ? (
          <button
            type="button"
            className="shrink-0 rounded-md px-1.5 py-1 text-xs font-medium text-label-secondary transition hover:bg-fill-tertiary hover:text-status-red"
            onClick={onClear}
          >
            Leeren
          </button>
        ) : null}
      </header>

      {lastSale ? (
        <div className="mx-3 mt-3 rounded-xl border border-status-green/35 bg-[color-mix(in_srgb,var(--eh-green)_12%,var(--eh-bg-card))] px-3 py-2.5">
          <p className="text-sm font-medium text-label">Verkauf abgeschlossen</p>
          <p className="mt-0.5 font-mono text-xs text-label-secondary">{lastSale.invoiceNumber}</p>
          <p className="mt-1 text-sm tabular-nums text-label">
            {lastSale.amountLabel}
            <span className="text-label-secondary"> · {lastSale.paymentLabel}</span>
          </p>
          {lastSale.pickupNote ? (
            <p
              className={clsx(
                "mt-1.5 text-xs",
                lastSale.pickupNote.ok ? "text-status-green" : "text-status-orange",
              )}
            >
              {lastSale.pickupNote.text}
            </p>
          ) : null}
          <Link to="/invoices" className="mt-1.5 inline-block text-xs font-medium text-accent">
            Zur Rechnungsübersicht
          </Link>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {lines.length === 0 ? (
          <div className="flex min-h-[10rem] flex-col items-center justify-center px-4 py-8 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-fill-tertiary text-label-tertiary">
              <ShoppingBag size={22} strokeWidth={1.75} aria-hidden />
            </span>
            <p className="mt-3 text-sm font-medium text-label">Noch nichts im Warenkorb</p>
            <p className="mt-1 max-w-[16rem] text-xs leading-relaxed text-label-secondary">{emptyHint}</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {lines.map((line) => (
              <li key={line.lineKey} className="rounded-xl bg-fill-quaternary px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium leading-tight text-label">{line.name}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-label-secondary">
                      {line.articleNumber}
                      {line.unitGrossLabel ? ` · ${line.unitGrossLabel}` : ""}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums text-label">
                    {line.lineTotalLabel ?? "—"}
                  </p>
                </div>
                {line.pickupDate ? (
                  <label className="mt-2 flex items-center gap-2 text-xs text-label-secondary">
                    <span className="shrink-0">Abholung</span>
                    <input
                      type="date"
                      className="eh-input min-w-0 flex-1 py-1 text-xs"
                      value={line.pickupDate}
                      min={today}
                      onChange={(e) => onPickupDate(line.lineKey, e.target.value)}
                    />
                  </label>
                ) : null}
                <div className="mt-2 flex items-center justify-between">
                  <div className="inline-flex items-center rounded-full bg-card p-0.5 shadow-ios">
                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-full text-lg leading-none text-label transition hover:bg-fill-tertiary"
                      aria-label="Menge verringern"
                      onClick={() => onAdjustQty(line.lineKey, -1)}
                    >
                      −
                    </button>
                    <span className="min-w-[1.75rem] text-center text-sm font-semibold tabular-nums">
                      {line.quantity}
                    </span>
                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-full text-lg leading-none text-label transition hover:bg-fill-tertiary"
                      aria-label="Menge erhöhen"
                      onClick={() => onAdjustQty(line.lineKey, 1)}
                    >
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-label-tertiary transition hover:bg-card hover:text-status-red"
                    aria-label={`${line.name} entfernen`}
                    onClick={() => onRemove(line.lineKey)}
                  >
                    <Trash2 size={16} aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        data-tour="pos-payment"
        className={clsx(
          "border-t border-separator bg-card/95 p-3 backdrop-blur-md lg:p-4",
          dockPayment &&
            "max-lg:fixed max-lg:inset-x-0 max-lg:z-20 max-lg:shadow-ios-lg",
          dockPayment && dockInset === "panel" && "max-lg:pb-[var(--eh-safe-bottom)]",
        )}
        style={
          dockPayment
            ? {
                bottom:
                  dockInset === "panel"
                    ? "0px"
                    : "calc(var(--eh-bottom-nav-height) + var(--eh-safe-bottom))",
              }
            : undefined
        }
      >
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-secondary">
              Zu zahlen
            </p>
            <p className="text-xl font-semibold tabular-nums tracking-tight text-label lg:text-2xl">
              {totalLabel}
            </p>
          </div>
          <p className="pb-1 text-xs text-label-secondary">inkl. MwSt.</p>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            type="button"
            size="lg"
            className="w-full"
            leftIcon={<Banknote size={18} aria-hidden />}
            disabled={!canPay || submitting}
            onClick={onCash}
          >
            Bar
            <span className="ml-1.5 rounded border border-current/25 px-1 text-[10px] font-semibold leading-none opacity-80">
              F2
            </span>
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            leftIcon={<CreditCard size={18} aria-hidden />}
            disabled={!canPay || submitting}
            onClick={onCard}
          >
            Karte
          </Button>
        </div>

        <p className="mt-2.5 hidden text-center text-[11px] leading-relaxed text-label-tertiary lg:block">
          {!saleReady
            ? "Zahlung folgt, sobald ein Gast oder Laufkunde gewählt ist."
            : lines.length === 0
              ? "Artikel hinzufügen, dann Bar oder Karte."
              : zvtConfigured
                ? "Karte startet das EC-Terminal."
                : "EC-Terminal nicht eingerichtet – Karte wird manuell verbucht."}
        </p>
      </div>
    </aside>
  );
}
